/**
 * Lightweight rich-text renderer that handles URLs, markdown links, and
 * `**bold**`: `<RichText>`, `<BulletText>`.
 *
 * `<RichText>` recursively splits a string on URL patterns, markdown
 * `[title](url)` links, and `**bold**` markers, rendering each segment with the
 * appropriate component. The `callbacks` prop maps `$name` references in
 * markdown links to `() => void` handlers, enabling in-text action links
 * without full URL navigation.
 */

import { useTheme } from '@repo/theme';
import type React from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import { Text } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { EMPTY_OBJECT } from 'util/stdtypes';

import { HorizBox } from './basics';
import type { TextType } from './text';
import { LinkText, Paragraph } from './text';
import { useTranslation } from './translation';
import { TextButton } from './ui/text-button';
export const urlRegex = /(http:\/\/|https:\/\/)[-a-zA-Z0-9.-]{2,256}\.[a-z]{2,4}([\/?](\.?[-\w@:%+~#?&\/=]+)*)?/gi;
const markdownLinkRegex = /\[([^\]]+)\]\(([^\)]+)\)/;
const markdownBoldRegex = /\*\*([^\*]+)\*\*/;

function trimUrl(url: string): string {
    if (url.length > 40) {
        return url.slice(0, 40) + '...';
    } else {
        return url;
    }
}

function addPrefixToUrl(url: string): string {
    if (!url.startsWith('http')) {
        return 'http://' + url;
    } else {
        return url;
    }
}

function removeTrailingPeriod(url: string): string {
    if (url[url.length - 1] === '.') {
        return url.slice(0, -1);
    } else {
        return url;
    }
}

const textButtonFontSizes: Record<TextType, string> = { '2xl': '16px', large: '16px', small: '14px', tiny: '12px' };

type RichTextPartProps = {
    text: string;
    color: string;
    linkColor: string;
    callbackColor?: string;
    type?: TextType;
    lineHeight?: number;
    callbacks?: Record<string, () => void>;
};

function renderMarkdownLink(props: RichTextPartProps): React.ReactElement | null {
    const { text, color, linkColor, callbackColor, type, lineHeight, callbacks = {} } = props;
    const mMarkdown = text.match(markdownLinkRegex);
    if (!mMarkdown || mMarkdown.length === 0) return null;
    const mdLink = mMarkdown[0];
    const title = mMarkdown[1];
    const url = mMarkdown[2];
    const start = text.indexOf(mdLink);
    const before = text.slice(0, start);
    const after = text.slice(start + mdLink.length);
    const textButtonStyle = type ? { style: { fontSize: textButtonFontSizes[type] } } : {};
    if (url.startsWith('$')) {
        const callback: (() => void) | undefined = callbacks[url.slice(1)];
        if (callback) {
            return (
                <Text>
                    <RichTextPart {...props} text={before} />
                    <TextButton {...textButtonStyle} colorOverride={callbackColor ?? color} onPress={callback} text={title} />
                    <RichTextPart {...props} text={after} />
                </Text>
            );
        } else {
            return (
                <Text>
                    <RichTextPart {...props} text={before} />
                    <Paragraph lineHeight={lineHeight} text={title} type={type} />
                    <RichTextPart {...props} text={after} />
                </Text>
            );
        }
    } else {
        return (
            <Text>
                <RichTextPart {...props} text={before} />
                <LinkText color={linkColor} text={title} type={type} url={url} />
                <RichTextPart {...props} text={after} />
            </Text>
        );
    }
}

function renderUrl(props: RichTextPartProps): React.ReactElement | null {
    const { text, linkColor, type } = props;
    const mUrl = text.match(urlRegex);
    if (!mUrl || mUrl.length === 0) return null;
    const url = mUrl[0];
    const linkUrl = removeTrailingPeriod(addPrefixToUrl(url));
    const start = text.indexOf(url);
    const before = text.slice(0, start);
    const after = text.slice(start + url.length);
    return (
        <Text>
            <RichTextPart {...props} text={before} />
            <LinkText color={linkColor} text={trimUrl(url)} type={type} url={linkUrl} />
            <RichTextPart {...props} text={after} />
        </Text>
    );
}

function renderBold(props: RichTextPartProps): React.ReactElement | null {
    const { text, color, type, lineHeight } = props;
    const mBold = text.match(markdownBoldRegex);
    if (!mBold || mBold.length === 0) return null;
    const boldText = mBold[1];
    const start = text.indexOf(mBold[0]);
    const before = text.slice(0, start);
    const after = text.slice(start + mBold[0].length);
    return (
        <Text>
            <RichTextPart {...props} text={before} />
            <Paragraph color={color} lineHeight={lineHeight} strong text={boldText} type={type} />
            <RichTextPart {...props} text={after} />
        </Text>
    );
}

function RichTextPart(props: RichTextPartProps) {
    const { text, color, type = 'small', lineHeight } = props;
    const propsWithDefault = { ...props, type };
    if (!text) return null;
    return (
        renderMarkdownLink(propsWithDefault) ??
        renderUrl(propsWithDefault) ??
        renderBold(propsWithDefault) ?? <Paragraph color={color} lineHeight={lineHeight} text={text} type={type} />
    );
}

export type RichTextProps = {
    text?: string;
    numberOfLines?: number;
    type?: TextType;
    lineHeight?: number;
    color?: string;
    linkColor?: string;
    callbackColor?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    style?: StyleProp<TextStyle>;
    callbacks?: Record<string, any>;
    suffixText?: string;
};

export function RichText({
    text,
    numberOfLines,
    type,
    lineHeight,
    color,
    linkColor,
    callbackColor,
    label,
    formatParams,
    style = EMPTY_OBJECT,
    callbacks = EMPTY_OBJECT,
    suffixText = '',
}: RichTextProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const resolvedLinkColor = linkColor ?? colors.colorTextPrimary;
    const tText = useTranslation(label, formatParams);
    return (
        <Text numberOfLines={numberOfLines} style={style}>
            <RichTextPart
                callbackColor={callbackColor}
                callbacks={callbacks}
                color={resolvedColor}
                lineHeight={lineHeight}
                linkColor={resolvedLinkColor}
                text={(text ?? tText) + suffixText}
                type={type}
            />
        </Text>
    );
}

export type BulletTextProps = { label?: TLabel; text?: string; color?: string };

export function BulletText({ label, text, color }: BulletTextProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    if (!label && !text) {
        return null;
    }
    return (
        <HorizBox gap={10}>
            <RichText color={resolvedColor} text='•' />
            <RichText color={resolvedColor} label={label} text={text} />
        </HorizBox>
    );
}
